import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Trash2, MessageCircle } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { useCart, cartWhatsappUrl, whatsappConfigured } from "@/context/CartContext";
import { formatPrice } from "@/config/products";

/**
 * Focus trap sencillo: tab-ciclo dentro del drawer, restaurando el foco
 * al elemento que abrió el carrito cuando se cierra.
 */
function useFocusTrap(active, containerRef) {
  useEffect(() => {
    if (!active) return;
    const previouslyFocused = document.activeElement;
    // Enfocar el primer elemento tabulable al abrir.
    const focusables = () =>
      Array.from(
        containerRef.current?.querySelectorAll(
          'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])'
        ) || []
      ).filter((el) => !el.hasAttribute("data-focus-skip"));
    const first = focusables()[0];
    first?.focus();

    const onKeyDown = (e) => {
      if (e.key !== "Tab") return;
      const nodes = focusables();
      if (!nodes.length) return;
      const firstEl = nodes[0];
      const lastEl = nodes[nodes.length - 1];
      if (e.shiftKey && document.activeElement === firstEl) {
        e.preventDefault();
        lastEl.focus();
      } else if (!e.shiftKey && document.activeElement === lastEl) {
        e.preventDefault();
        firstEl.focus();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      if (previouslyFocused && typeof previouslyFocused.focus === "function") {
        previouslyFocused.focus();
      }
    };
  }, [active, containerRef]);
}

export default function CartDrawer({ onAbandon }) {
  const { t } = useLanguage();
  const { open, setOpen, items, removeItem, updateQty, total, clear } = useCart();
  const [highlightId, setHighlightId] = useState(null);

  // Escucha kelo:cart-highlight → aplica glow al item recién añadido por ~1s
  useEffect(() => {
    const onHi = (e) => {
      const { productId, size } = e.detail || {};
      const match = items.find(
        (i) => i.id === productId && (!size || i.size === size)
      );
      if (!match) return;
      setHighlightId(match.key);
      window.setTimeout(() => setHighlightId(null), 1100);
    };
    window.addEventListener("kelo:cart-highlight", onHi);
    return () => window.removeEventListener("kelo:cart-highlight", onHi);
  }, [items]);
  const waOn = whatsappConfigured();
  const drawerRef = useRef(null);

  // iOS-robust scroll lock: preserva la posición de scroll fijando el body.
  useEffect(() => {
    if (!open) return;
    const scrollY = window.scrollY;
    const body = document.body;
    const prevPos = body.style.position;
    const prevTop = body.style.top;
    const prevWidth = body.style.width;
    body.style.position = "fixed";
    body.style.top = `-${scrollY}px`;
    body.style.width = "100%";
    // Inert en el resto de la página para asistive tech.
    const main = document.querySelector("main") || document.getElementById("root");
    if (main) main.setAttribute("aria-hidden", "true");

    const onKey = (e) => {
      if (e.key === "Escape") closeCart();
    };
    document.addEventListener("keydown", onKey);
    return () => {
      body.style.position = prevPos;
      body.style.top = prevTop;
      body.style.width = prevWidth;
      window.scrollTo(0, scrollY);
      if (main) main.removeAttribute("aria-hidden");
      document.removeEventListener("keydown", onKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useFocusTrap(open, drawerRef);

  const closeCart = () => {
    try {
      const hasItems = items.length > 0;
      const alreadyThisSession = sessionStorage.getItem("kelo_cart_abandon_shown") === "1";
      const alreadySubmitted = localStorage.getItem("kelo_lead_submitted") === "1";
      const lastAbandonAt = Number(localStorage.getItem("kelo_cart_abandon_at") || 0);
      const withinCooldown = lastAbandonAt && Date.now() - lastAbandonAt < 24 * 60 * 60 * 1000;

      if (
        hasItems &&
        !alreadyThisSession &&
        !alreadySubmitted &&
        !withinCooldown &&
        typeof onAbandon === "function"
      ) {
        sessionStorage.setItem("kelo_cart_abandon_shown", "1");
        localStorage.setItem("kelo_cart_abandon_at", String(Date.now()));
        window.setTimeout(() => onAbandon("cart_abandon"), 500);
      }
    } catch (_e) {
      /* storage disabled — silently fail */
    }
    setOpen(false);
  };

  const checkout = () => {
    if (!items.length || !waOn) return;
    sessionStorage.setItem("kelo_cart_abandon_shown", "1");
    window.open(cartWhatsappUrl(items, total, t), "_blank");
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
            onClick={closeCart}
            className="fixed inset-0 z-[60] bg-black/40 backdrop-blur-sm"
          />
          <motion.aside
            ref={drawerRef}
            data-testid="cart-drawer"
            role="dialog"
            aria-modal="true"
            aria-label={t.cart.title}
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{
              type: "spring",
              stiffness: 220,
              damping: 32,
              mass: 0.85,
            }}
            drag="x"
            dragDirectionLock
            dragConstraints={{ left: 0, right: 400 }}
            dragElastic={{ left: 0, right: 0.35 }}
            onDragEnd={(_, info) => {
              // Velocity-aware close: si el usuario arrastra >120px o velocity >500px/s hacia la derecha, cerramos.
              if (info.offset.x > 120 || info.velocity.x > 500) {
                closeCart();
              }
            }}
            className="fixed right-0 top-0 z-[70] flex h-[100dvh] w-full max-w-md flex-col border-l border-[#eee] bg-white touch-pan-y"
            style={{ paddingTop: "env(safe-area-inset-top, 0px)" }}
          >
            <div className="flex items-center justify-between border-b border-[#eee] px-6 py-5">
              <h3 className="font-serif-display text-lg text-[#111]">{t.cart.title}</h3>
              <button
                data-testid="cart-close"
                onClick={closeCart}
                className="press flex h-11 w-11 items-center justify-center rounded-full border border-[#e2e2e2] text-[#111] transition-colors hover:border-[#111]"
                aria-label={t.cart.close || "Close"}
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto overscroll-contain px-6 py-4">
              {items.length === 0 ? (
                <p data-testid="cart-empty" className="mt-16 text-center font-body text-sm text-[#999]">
                  {t.cart.empty}
                </p>
              ) : (
                <ul className="space-y-5">
                  <AnimatePresence initial={false}>
                    {items.map((i) => (
                      <motion.li
                        key={i.key}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 6 }}
                        transition={{ duration: 0.24, ease: [0.22, 1, 0.36, 1] }}
                        data-testid={`cart-item-${i.key}`}
                        data-highlight={highlightId === i.key ? "1" : "0"}
                        className="cart-item flex items-start justify-between gap-4 border-b border-[#f2f2f2] pb-5"
                      >
                        <div>
                          <p className="font-body text-sm font-medium text-[#111]">
                            {t.products[i.productKey]?.name || i.productKey}
                          </p>
                          <p className="mt-1 font-body text-xs text-[#999]">
                            {t.cart.size}: {i.size}
                          </p>
                          <div className="mt-2 flex w-fit items-center rounded-full border border-[#e2e2e2]">
                            <button
                              data-testid={`cart-minus-${i.key}`}
                              onClick={() => updateQty(i.key, i.qty - 1)}
                              className="press flex h-11 w-11 items-center justify-center text-lg text-[#666] transition-colors hover:text-[#111]"
                              aria-label={t.cart.decrease || "Decrease"}
                            >
                              −
                            </button>
                            <span
                              className="min-w-[1.5rem] text-center font-body text-sm tabular-nums text-[#111]"
                              aria-live="polite"
                            >
                              {i.qty}
                            </span>
                            <button
                              data-testid={`cart-plus-${i.key}`}
                              onClick={() => updateQty(i.key, i.qty + 1)}
                              className="press flex h-11 w-11 items-center justify-center text-lg text-[#666] transition-colors hover:text-[#111]"
                              aria-label={t.cart.increase || "Increase"}
                            >
                              +
                            </button>
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-3">
                          {/* Precio con ancho reservado + transición suave (no layout jump) */}
                          <span
                            className="min-w-[72px] text-right font-body text-sm tabular-nums text-[#111] transition-opacity duration-200"
                            aria-live="polite"
                          >
                            <AnimatePresence mode="popLayout" initial={false}>
                              <motion.span
                                key={i.price * i.qty}
                                initial={{ opacity: 0, y: 4 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -4 }}
                                transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
                                className="inline-block"
                              >
                                {formatPrice(i.price * i.qty)}
                              </motion.span>
                            </AnimatePresence>
                          </span>
                          <button
                            data-testid={`cart-remove-${i.key}`}
                            onClick={() => removeItem(i.key)}
                            className="press flex h-11 w-11 items-center justify-center text-[#bbb] transition-colors hover:text-[#111]"
                            aria-label={t.cart.remove}
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </motion.li>
                    ))}
                  </AnimatePresence>
                </ul>
              )}
            </div>

            <div
              className="border-t border-[#eee] px-6 py-6"
              style={{ paddingBottom: "calc(1.5rem + env(safe-area-inset-bottom, 0px))" }}
            >
              <div className="mb-3 flex items-center justify-between">
                <span className="font-body text-sm text-[#777]">{t.cart.total}</span>
                <span
                  data-testid="cart-total"
                  className="font-serif-display text-2xl tabular-nums text-[#111]"
                >
                  <AnimatePresence mode="popLayout" initial={false}>
                    <motion.span
                      key={total}
                      initial={{ opacity: 0, y: 4 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -4 }}
                      transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
                      className="inline-block"
                    >
                      {formatPrice(total)}
                    </motion.span>
                  </AnimatePresence>
                </span>
              </div>
              <p className="mb-4 font-body text-xs text-[#999]">{t.cart.shippingNote}</p>
              {waOn && (
                <button
                  data-testid="cart-checkout"
                  onClick={checkout}
                  disabled={!items.length}
                  className="press flex w-full items-center justify-center gap-2 rounded-full bg-[#111] px-6 py-3.5 font-body text-sm font-medium text-white transition-colors duration-200 hover:bg-[#2a2a2a] disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <MessageCircle className="h-4 w-4" /> {t.cart.checkout}
                </button>
              )}
              {items.length > 0 && (
                <button
                  data-testid="cart-clear"
                  onClick={clear}
                  className="mt-3 w-full py-2 text-center font-body text-xs text-[#999] transition-colors hover:text-[#111]"
                >
                  {t.cart.clear}
                </button>
              )}
            </div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
